// src/data/unleashedProducts.ts
// -----------------------------
// Repository for `unleashed_products` (db-64) — the editable copy of the
// Unleashed product catalogue. Backs the Manage → Unleashed → Products admin
// screen and (later) the product-mapping pick-list. Typed via getTypedClient().
//
// The table is small (~245 rows), but filtering is still done SERVER-side with an
// explicit limit — the same discipline as `schools`/`suppliers` (PostgREST
// silently caps an un-limited select at 1,000 rows), so it stays correct as the
// catalogue grows.

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export interface AdminProductRow {
  id: number;
  product_code: string;
  product_description: string | null;
  product_group: string | null;
  base_pack: string | null;
  base_unit: string | null;
  deactivated: boolean;
}

// Typed as `string` (not a literal) so supabase-js doesn't deep-parse the select.
const PRODUCT_COLS: string =
  "id, product_code, product_description, product_group, base_pack, base_unit, deactivated";

export const PRODUCTS_LIST_LIMIT = 1000;

export interface ListProductsOptions {
  // Case-insensitive substring match on code OR description.
  search?: string;
  // Exact product_group filter (e.g. "Shoe", "Uniform").
  group?: string;
  // Page size (defaults to PRODUCTS_LIST_LIMIT).
  limit?: number;
}

// List products for the admin console, code-ordered, filters applied SERVER-side.
// Deactivated rows are NOT filtered here — the admin console shows them (badge /
// reactivate toggle). Returns null on error / no client.
export async function listProducts(
  opts: ListProductsOptions = {}
): Promise<AdminProductRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const limit = opts.limit ?? PRODUCTS_LIST_LIMIT;
  let q = c.from("unleashed_products").select(PRODUCT_COLS);
  if (opts.group) q = q.eq("product_group", opts.group);
  // Strip PostgREST .or() syntax characters from the user's search text so a
  // stray comma/paren/percent can't break (or inject into) the filter expression.
  const s = (opts.search ?? "")
    .replace(/[,()%]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (s) q = q.or(`product_code.ilike.%${s}%,product_description.ilike.%${s}%`);
  const { data, error } = await q.order("product_code").limit(limit);
  if (error) {
    logger.warn("listProducts failed:", error.message);
    return null;
  }
  return (data as unknown as AdminProductRow[]) ?? [];
}

// A code → description lookup over the whole catalogue, for callers that need to
// resolve a product code to its human description (e.g. the Top-up SO builder puts
// the description in the bulk summary SO line comment). Deactivated rows are kept
// (a description is still valid). Returns an empty Map on error / no client.
export async function getProductDescriptionMap(): Promise<Map<string, string>> {
  const rows = await listProducts({ limit: PRODUCTS_LIST_LIMIT });
  const m = new Map<string, string>();
  for (const r of rows ?? []) {
    if (r.product_code && r.product_description) m.set(r.product_code, r.product_description);
  }
  return m;
}

type ProductPatch = Database["public"]["Tables"]["unleashed_products"]["Update"];
type ProductInsert = Database["public"]["Tables"]["unleashed_products"]["Insert"];
type WriteResult = { error: { message: string } | null };

// Update a product by surrogate id (the admin edit modal + deactivate/reactivate
// toggle). Takes a loose patch object (admin form state) cast to the generated
// Update shape, per the repo write convention. Also stamps updated_at. Logs on
// error and returns { error }; a missing client returns a null error.
export async function updateProductById(
  id: number | undefined,
  patch: object
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const body = { ...(patch as ProductPatch), updated_at: new Date().toISOString() };
  const { error } = await c
    .from("unleashed_products")
    .update(body)
    .eq("id", id ?? 0);
  if (error) logger.warn("updateProductById failed:", error.message);
  return { error };
}

// Create a product (the admin "+ New product" form). Takes a loose object cast to
// the generated Insert shape, per the repo write convention. Logs on error and
// returns { error }.
export async function createProduct(row: object): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c.from("unleashed_products").insert(row as ProductInsert);
  if (error) logger.warn("createProduct failed:", error.message);
  return { error };
}
